/**
 * React Query hooks for actions API
 *
 * Note: The api module has a response interceptor that returns response.data directly.
 * Do NOT do `return response.data` in queryFn - just `return response`.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';

/**
 * Get all registered actions
 */
export function useActions(includeDisabled = false) {
  return useQuery({
    queryKey: ['actions', { includeDisabled }],
    queryFn: async () => {
      const params = includeDisabled ? '?include_disabled=true' : '';
      const result = await api.get(`/actions${params}`);
      // Backend returns { success: true, data: [...] }
      // Interceptor returns the whole object, so extract .data
      return result.data || result;
    }
  });
}

/**
 * Get pending action suggestions
 */
export function usePendingActionSuggestions() {
  return useQuery({
    queryKey: ['actions', 'suggestions', 'pending'],
    queryFn: async () => {
      const result = await api.get('/actions/suggestions/pending');
      // Backend may return { success: true, data: [...] }
      return result.data || result;
    },
    refetchInterval: 30000 // Refetch every 30s
  });
}

/**
 * Approve and execute a pending action
 */
export function useApproveAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, always = false }) => {
      const response = await api.post(`/actions/suggestions/${id}/approve`, { always });
      return response;
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['actions', 'suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['actions', 'executions'] });
      queryClient.invalidateQueries({ queryKey: ['secretary', 'digest'] });
      queryClient.invalidateQueries({ queryKey: ['secretary', 'latest-report'] });
      queryClient.invalidateQueries({ queryKey: ['secretary', 'reports'] });
      queryClient.invalidateQueries({ queryKey: ['emails'] });
    }
  });
}

/**
 * Reject a pending action
 */
export function useRejectAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, never = false }) => {
      const response = await api.post(`/actions/suggestions/${id}/reject`, { never });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['actions', 'suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['secretary', 'digest'] });
      queryClient.invalidateQueries({ queryKey: ['secretary', 'latest-report'] });
      queryClient.invalidateQueries({ queryKey: ['secretary', 'reports'] });
    }
  });
}

/**
 * Execute an action directly (with approval if required)
 */
export function useExecuteAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ actionName, emailIds = [], params = {} }) => {
      const response = await api.post('/actions/execute', {
        action_name: actionName,
        email_ids: emailIds,
        params
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['actions', 'executions'] });
      queryClient.invalidateQueries({ queryKey: ['emails'] });
    }
  });
}

/**
 * Get pre-approvals for user
 */
export function usePreApprovals(userEmail = 'user@example.com') {
  return useQuery({
    queryKey: ['actions', 'pre-approvals', { userEmail }],
    queryFn: async () => {
      const result = await api.get(`/actions/pre-approvals?user_email=${userEmail}`);
      // Backend may return { success: true, data: [...] } or array directly
      return result.data || result || [];
    }
  });
}

/**
 * Create pre-approval
 */
export function useCreatePreApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ actionName, userEmail, conditions, autoApprove = true }) => {
      const response = await api.post('/actions/pre-approvals', {
        action_name: actionName,
        user_email: userEmail,
        conditions,
        auto_approve: autoApprove
      });
      return response;
    },
    onSuccess: () => {
      // Invalidate all pre-approval queries (including specific user queries)
      queryClient.invalidateQueries({
        queryKey: ['actions', 'pre-approvals'],
        exact: false
      });
    }
  });
}

/**
 * Delete pre-approval
 */
export function useDeletePreApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/actions/pre-approvals/${id}`);
      return response;
    },
    onSuccess: () => {
      // Invalidate all pre-approval queries (including specific user queries)
      queryClient.invalidateQueries({
        queryKey: ['actions', 'pre-approvals'],
        exact: false
      });
    }
  });
}

/**
 * Get action execution history
 */
export function useActionExecutions({ limit = 50, offset = 0, actionName } = {}) {
  return useQuery({
    queryKey: ['actions', 'executions', { limit, offset, actionName }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', offset);
      if (actionName) params.append('action_name', actionName);

      const result = await api.get(`/actions/executions?${params}`);
      // Backend may return { success: true, data: [...] }
      return result.data || result;
    }
  });
}
